import { useTranslation } from 'react-i18next';
import { UseFormRegister } from 'react-hook-form';
import type { UpdateCompanyDto } from '@monorepo/shared';
import { FormInput } from '../ui/FormInput';
import { FormCheckbox } from '../ui/FormCheckbox';
import { VALIDATION_PATTERNS } from '../../constants/validation';

interface ChangeMainContactErrors {
  newMainContactFirstName?: { message?: string };
  newMainContactLastName?: { message?: string };
  newMainContactEmail?: { message?: string };
}

interface ChangeMainContactFormProps<T extends UpdateCompanyDto> {
  register: UseFormRegister<T>;
  errors: ChangeMainContactErrors;
  changeMainContact: boolean;
}

export const ChangeMainContactForm = <T extends UpdateCompanyDto>({
  register,
  errors,
  changeMainContact,
}: ChangeMainContactFormProps<T>) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <FormCheckbox
        label={t('company.change_main_contact.label')}
        {...register('changeMainContact' as Parameters<typeof register>[0])}
      />

      {changeMainContact && (
        <div className="ml-6 space-y-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-secondary mb-4">
            {t('company.change_main_contact.description')}
          </p>

          <FormInput
            label={t('company.change_main_contact.first_name.label')}
            type="text"
            placeholder={t('company.change_main_contact.first_name.placeholder')}
            error={errors.newMainContactFirstName?.message}
            required
            {...register('newMainContactFirstName' as Parameters<typeof register>[0], {
              required: changeMainContact
                ? t('company.change_main_contact.first_name.error.required')
                : false,
            })}
          />

          <FormInput
            label={t('company.change_main_contact.last_name.label')}
            type="text"
            placeholder={t('company.change_main_contact.last_name.placeholder')}
            error={errors.newMainContactLastName?.message}
            required
            {...register('newMainContactLastName' as Parameters<typeof register>[0], {
              required: changeMainContact
                ? t('company.change_main_contact.last_name.error.required')
                : false,
            })}
          />

          <FormInput
            label={t('company.change_main_contact.email.label')}
            type="email"
            placeholder={t('company.change_main_contact.email.placeholder')}
            error={errors.newMainContactEmail?.message}
            required
            {...register('newMainContactEmail' as Parameters<typeof register>[0], {
              required: changeMainContact
                ? t('company.change_main_contact.email.error.required')
                : false,
              pattern: {
                value: VALIDATION_PATTERNS.EMAIL,
                message: t('company.change_main_contact.email.error.invalid'),
              },
            })}
          />
        </div>
      )}
    </div>
  );
};
